import React, { useContext } from 'react'
import { Button, Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import tinycolor from 'tinycolor2'
import MetadataBlock from 'src/apps/Initiative/components/MetadataBlock'
import InitaitiveTagsBlock from 'src/apps/Initiative/components/TagsBlock'
import InitiativeTypeLabel from 'src/apps/Initiative/components/TypeLabel'
import MessageFeed from 'src/apps/Messages/components/Feed'
import InitiativeMoreButton from 'src/apps/Initiative/components/MoreButton'
import InitiativeStatusBanner from 'src/apps/Initiative/components/StatusBanner'
import ContentView from 'src/components/Content/View'
import UserListItemTile from 'src/components/User/ListItemTile'
import { useOrganization } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import PageUIContext from 'src/libraries/contexts/PageUI'
import ThemeContext from 'src/libraries/contexts/Theme'

const BlogView = ({ displayMode, children }) => {
   const isFullscreen = displayMode === 'fullscreen'
   const theme = useContext(ThemeContext)

   const { initiative } = useContext(InitiativeHierarchyContext)
   const { organization } = useOrganization()

   const initiativeBody = initiative.body.elements
   const coverMediaUrl = initiative.coverMedia?.file.thumbnailUrlW1600
   const coverMediaBlurredUrl = initiative.coverMedia?.file.thumbnailUrlW10Blur
   const blurredBackgroundImage = isFullscreen
      ? `url(${coverMediaBlurredUrl})[a-d]`
      : `url(${coverMediaBlurredUrl})`

   const hasCoverMedia = !!coverMediaUrl
   const textColor = coverMediaUrl ? '#fff' : '#000'

   return (
      <>
         <Helmet title={`${initiative.name} | ${organization.name}`} />
         <View
            paddingTop={isFullscreen && theme.viewportMarginTop}
            paddingLeft={theme.viewportMarginLeft}
            paddingRight={theme.viewportMarginRight}>
            <InitiativeStatusBanner />
            <View display="flex[d-f]">
               <View
                  position="relative"
                  flex="0 0 auto"
                  float="left"
                  width={isFullscreen
                     ? '100%[a-e] calc(((100% - 1700px) / 2) + 780px)[f]'
                     : '100%[a-c] 35%[d-f]'
                  }
                  maxWidth={isFullscreen && '500px[d] 45%[e]'}
                  paddingLeft="calc((100% - 1700px) / 2)[f]"
                  backgroundColor={hasCoverMedia && '#111[a-d]'}
                  backgroundImage={hasCoverMedia && blurredBackgroundImage}
                  backgroundPosition="center center"
                  backgroundSize="cover">
                  {hasCoverMedia && (
                     <View
                        position="absolute"
                        top="0px"
                        left="0px"
                        right="0px"
                        bottom="0px"
                        backgroundColor={isFullscreen
                           ? 'rgba(0,0,0,0.65)[a-d]'
                           : 'rgba(0,0,0,0.65)'
                        }
                     />
                  )}
                  <View
                     position="relative"
                     top="0px"
                     width="100%"
                     zIndex="2"
                     maxWidth="780px[f]"
                     paddingLeft={isFullscreen && '0px[a-d] 100px[e] 150px[f]'}
                     paddingRight={isFullscreen && '0px[a-d] 0px[e-f]'}
                     paddingVertical={isFullscreen && '50px[e-f]'}>
                     {hasCoverMedia && (
                        <View
                           float="left"
                           width="100%"
                           paddingTop="56.25%"
                           backgroundImage={`url(${coverMediaUrl})`}
                           backgroundSize="cover"
                           backgroundPosition="center center"
                           style={{
                              borderTopLeftRadius: isFullscreen && '5px',
                              borderTopRightRadius: isFullscreen && '5px',
                              overflow: 'hidden'
                           }}
                        />
                     )}
                     <View
                        position="relative"
                        float="left"
                        width="100%"
                        height="100%[d]"
                        minHeight="50vh[d-f]"
                        marginBottom="50px[d-f]"
                        backgroundColor={isFullscreen && hasCoverMedia && '#111[e-f]'}
                        backgroundImage={isFullscreen && hasCoverMedia && `url(${coverMediaBlurredUrl})[e-f]`}
                        backgroundPosition="center center"
                        backgroundSize="cover"
                        paddingVertical={isFullscreen
                           ? '30px[a-b] 50px[c-f]'
                           : '30px[a-b] 50px[c-f]'
                        }
                        paddingHorizontal={isFullscreen
                           ? '18px[a] 9vw[b] 6vw[c] 4vw[d] 50px[e-f]'
                           : '25px[a-b] 40px[c-d] 50px[e-f]'}
                        style={{
                           borderBottomLeftRadius: '5px',
                           borderBottomRightRadius: '5px',
                           overflow: 'hidden'
                        }}>
                        {isFullscreen && hasCoverMedia && (
                           <View
                              position="absolute"
                              top="0px"
                              left="0px"
                              right="0px"
                              bottom="0px"
                              backgroundColor="rgba(0,0,0,0.65)[e-f]"
                           />
                        )}
                        <View
                           position="relative"
                           top="0px"
                           width="100%"
                           zIndex="2">
                           <InitiativeTypeLabel />
                           <Spacer size="0.5" />
                           <Text
                              lineHeight="120%"
                              size="8[a-b] 9[c-d] 10[e-f]"
                              letterSpacing="-0.5px"
                              weight="medium"
                              color={textColor}>
                              {initiative.name}
                           </Text>
                           {initiative.subtitle && (
                              <View opacity="0.7">
                                 <Spacer size="1.5" />
                                 <Text
                                    color={textColor}
                                    weight="light"
                                    size="6[a-d] 7[e-f]">
                                    {initiative.subtitle}
                                 </Text>
                              </View>
                           )}
                           <Spacer size="2" />
                           <View opacity="0.5">
                              <Text color={textColor} size="2">
                                 By
                                 &nbsp;
                                 <span
                                    style={{
                                       opacity: '0.8',
                                       fontWeight: '600 !important'
                                    }}>
                                    {initiative.addedBy.fullName}
                                 </span>
                                 &nbsp;&nbsp;&#183;&nbsp;&nbsp;
                                 {initiative.dateAdded}
                              </Text>
                           </View>
                           {initiative.currentUserCanEdit && (
                              <View
                                 display="flex"
                                 justifyContent="flex-end"
                                 borderTop={hasCoverMedia
                                    ? `1px solid ${tinycolor(textColor).setAlpha('.15')}`
                                    : '1px solid #eee'
                                 }
                                 marginTop="50px[a-b] 70px[c-f]"
                                 paddingTop="25px">
                                 <InitiativeMoreButton
                                    color={hasCoverMedia ? 'rgba(255,255,255,0.1)' : '#eee'}
                                    iconColor={hasCoverMedia ? '#fff' : '#444'}
                                 />
                              </View>
                           )}
                        </View>
                     </View>
                  </View>
               </View>
               <View
                  float="left"
                  width={isFullscreen
                     ? '100%[a-c] calc(100% - 500px)[d] calc(100% - 45%)[e] calc(((100% - 1700px) / 2) + calc(1700px - 780px))[f]'
                     : '100%[a-c] 65%[d-f]'
                  }
                  minHeight="100vh[d-f]"
                  borderLeft={!hasCoverMedia && '1px solid #eee'}>
                  <View
                     maxWidth={isFullscreen && 'calc(1700px - 780px)[f]'}
                     paddingLeft={isFullscreen
                        ? '18px[a] 9vw[b] 6vw[c] 40px[d] 50px[e-f]'
                        : '25px[a-c] 40px[d] 50px[e-f]'
                     }
                     paddingRight={isFullscreen
                        ? '0px[a] 9vw[b] 6vw[c] 4vw[d] 100px[e] 150px[f]'
                        : '25px[a-c] 40px[d] 50px[e-f]'
                     }
                     paddingVertical={isFullscreen
                        ? '50px'
                        : '25px[a-c] 90px[d] 100px[e-f]'
                     }>
                     <View
                        float="left"
                        width="100%[a-d] calc(100% - 130px)[e-f]"
                        minHeight="90vh[e-f]"
                        borderRight="1px solid #eee[e-f]"
                        paddingRight="25px[e-f]">
                        {initiativeBody && (
                           <View
                              float="left"
                              width="100%"
                              paddingBottom="30px">
                              <Text
                                 size="2[a-c] 2.5[d] 2.5[e-f]"
                                 lineHeight="150%[a-d] 150%[e-f]"
                                 color="#555">
                                 <PageUIContext.Provider
                                    value={{
                                       contentMaxWidth: '100%',
                                       contentPaddingLeft: '0px',
                                       contentPaddingRight: '0px',
                                       contentPaddingVertical: '20px'
                                    }}>
                                    <ContentView elements={initiativeBody} />
                                 </PageUIContext.Provider>
                              </Text>
                           </View>
                        )}
                        {!initiativeBody && (
                           <View
                              float="left"
                              width="100%"
                              minHeight="60px">
                              <Text size="2" lineHeight="150%" color="#888">
                                 {`This ${initiative.type.nameSingular} has no content yet`}
                              </Text>
                           </View>
                        )}
                        <InitaitiveTagsBlock />
                        <View
                           float="left"
                           display="flex"
                           justifyContent="space-between"
                           alignItems="center"
                           borderTop="1px solid rgba(0,0,0,0.07)"
                           paddingVertical="20px[a-b] 25px[c-f]"
                           width="100%">
                           <View flex="1 1 auto">
                              <UserListItemTile
                                 id={initiative.addedBy.id}
                                 avatarMedia={initiative.addedBy.avatarMedia}
                                 fullName={initiative.addedBy.fullName}
                                 organization={initiative.addedBy.organization}
                                 position={initiative.addedBy.position}
                              />
                           </View>
                           <View flex="0 0 auto">
                              <Button
                                 linkTo={`/profile/${initiative.addedBy.id}`}
                                 width="120px"
                                 name="View Profile"
                                 size="sm"
                                 color="#eee"
                                 textColor="#333"
                              />
                           </View>
                        </View>
                        <View
                           float="left"
                           width="100%"
                           paddingBottom="50px"
                           borderTop="1px solid rgba(0,0,0,0.07)">
                           <View width="100%" paddingVertical="20px">
                              <Text size="0.8" weight="medium" transform="uppercase" color="#888">
                                 Comments
                              </Text>
                           </View>
                           <MessageFeed
                              currentUserCanModerate={initiative.currentUserCanModerate}
                              displayMode="conversation"
                              hasUnreadEntries={initiative.thread.hasUnreadEntries}
                              messageLastViewed={initiative.thread.messageLastViewed}
                           />
                        </View>
                     </View>
                     <View
                        display="none[a-d] block[e-f]"
                        float="left"
                        width="100%[a-d] 130px[e-f]"
                        paddingLeft="25px">
                        <MetadataBlock initiative={initiative} />
                     </View>
                  </View>
               </View>
            </View>
         </View>
         {children}
      </>
   )
}

BlogView.propTypes = {
   displayMode: PropTypes.oneOf(['fullscreen', 'modal'])
}

BlogView.defaultProps = {
   displayMode: 'fullscreen'
}

export default BlogView
